import { Component, ElementRef, Input, NgZone, OnChanges, OnInit, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { Lookup } from 'projects/shared/src/models/lookup';
import { LookupsService } from 'projects/shared/src/services/lookups.service';
import { debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-filter-section',
  standalone: false,
  templateUrl: './filter-section.component.html'
})
export class FilterSectionComponent implements OnInit, OnChanges {
  @Input() selectedValues: any[] | string | null = null;
  @ViewChildren('recordItem')
  recordItems!: QueryList<ElementRef<HTMLElement>>;
  @Input() multiCheckbox: boolean = false;
  @Input() hideSearch: boolean = false;
  @Input() title: string = '';
  @Input() searchPlaceholder: string = '';
  @Input() controlName: string = '';
  @Input() mainCategoryId: number | null = null;
  @Input() emptyMessage = 'general.No Data Found';
  searchControl = new FormControl('');
  recordsData: Lookup[] = [];
  search = '';

  pageNumber = 1;
  pageSize = 6;
  pageCount = 0;
  totalCount = 0;
  dataLoading = false;
  loading = false;

  previousRadioValue: any = null;

  constructor(
    private fb: FormBuilder,
    private rootForm: FormGroupDirective,
    private lookupService: LookupsService,
    private ngZone: NgZone
  ) { }

  ngOnChanges(changes: SimpleChanges): void {

    if (
      this.controlName === 'SubCategoryIds' &&
      changes['mainCategoryId'] &&
      !changes['mainCategoryId'].firstChange
    ) {

      this.recordsData = [];
      this.formArray.clear();

      this.loadData();

    }
  }

  ngOnInit() {
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(value => {

      this.search = value ?? '';

      this.loadData(true);

    });

    this.loadData(true);
  }

  get form(): FormGroup {
    return this.rootForm.control;
  }

  get formArray(): FormArray {
    return this.form.get(this.controlName) as FormArray;
  }


  loadData(reset = false): void {
    this.dataLoading = !this.loading;
    if (reset) {
      this.pageNumber = 1;
      this.recordsData = [];
      if (this.multiCheckbox) {
        this.clearCheckboxControls();
      }
    }
    const query = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      searchText: this.search
    };
    switch (this.controlName) {

      case 'MainCategoryId':
        this.lookupService.getAllMainCategories(query).subscribe(res => {
          this.callbackApi(res);
        });
        break;
      case 'SubCategoryIds':
        if (this.form.get('MainCategoryId')?.value) {
          query['mainCatgeoryId'] = this.form.get('MainCategoryId')?.value;
        }
        this.lookupService.getAllSubCategories(query).subscribe(res => {
          this.callbackApi(res);
        });
        break;

      case 'BrandIds':
        this.lookupService.getAllBrands(query).subscribe(res => {
          this.callbackApi(res);
        });
        break;

      case 'CountryIds':
        this.lookupService.getListCountries(query).subscribe(res => {
          this.callbackApi(res);
        });
        break;

      case 'CityIds':
        this.lookupService.getAllSaudiCities(query).subscribe(res => {
          this.callbackApi(res);
        });
        break;

      case 'ChannelsIds':
        this.lookupService.getMonitoringTargetChannels().subscribe(res => {
          this.callbackApi(res);
        });
        break;

      case 'FraudTypesIds':
        this.lookupService.getFraudTypes(query).subscribe(res => {
          this.callbackApi(res);
        });
        break;

      case 'OfferTypeId':
        this.lookupService.getLookupOfferTypes().subscribe(res => {
          this.callbackApi(res);
        });
        break;

      case 'EconomicOperatorId':
        this.lookupService.getAllOperatorsHasOffers(query).subscribe(res => {
          this.callbackApi(res);
        });
        break;

    }

  }

  callbackApi(res) {
    this.dataLoading = false;
    this.loading = false;
    let newItems = res;
    if (this.controlName === 'ChannelsIds' || this.controlName === 'OfferTypeId') {
      this.recordsData = res;
      this.totalCount = res.length;
      this.pageCount = 1;
    } else {
      this.totalCount = res.totalCount;
      newItems = res.data.filter(x =>
        !this.recordsData.some(r => r.id === x.id)
      );
      this.recordsData.push(...newItems);
      this.pageCount = Math.ceil(
        res.totalCount / this.pageSize
      );
    }
    if (this.multiCheckbox) {
      this.buildControls(newItems);
    }
    const oldLength = this.recordsData.length;
    // Wait until Angular renders the new records
    if (oldLength > 0 && newItems.length > 0) {
      setTimeout(() => {
        const element = this.recordItems.get(oldLength);

        if (element) {
          element.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 0);
    }
  }

  private clearCheckboxControls(): void {
    this.formArray.clear();
  }

  private buildControls(items: Lookup[]): void {
    const selectedIds = this.getSelectedIds();

    items.forEach(item => {
      this.formArray.push(
        this.fb.group({
          id: [item.id],
          checked: [selectedIds.includes(Number(item.id))]
        })
      );
    });
  }

  private getSelectedIds(): number[] {
    if (
      this.selectedValues === null ||
      this.selectedValues === undefined ||
      this.selectedValues === ''
    ) {
      return [];
    }

    const values = Array.isArray(this.selectedValues)
      ? this.selectedValues
      : [this.selectedValues];

    return values
      .map(value => Number(value))
      .filter(value => !Number.isNaN(value));
  }

  getNextPage() {
    if (this.pageNumber <= this.pageCount - 1) {
      this.loading = true;
      this.pageNumber += 1;
      this.loadData();
    }
  }

  toggleRadio(event: MouseEvent, id: any): void {
    const control = this.form.get(this.controlName);

    if (!control) {
      return;
    }

    // The control still contains the previously selected value
    if (control.value === id) {
      setTimeout(() => {
        control.setValue(null);
      });
    }
  }
}
